import { Badge, Button, Card, Collapse, Group, Select, Stack, Text } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Mermaid } from '../components/Mermaid';
import { RoadmapView } from '../components/RoadmapView';
import { useDeleteSimRoadmap, useRoadmapDetail, useRoadmaps } from '../hooks/use-content';
import { useI18n } from '../i18n';
import { buildMermaid } from '../lib/roadmap-mermaid';
import { errorMessage } from '../lib/ui';

/**
 * Short absolute date-time (e.g. "29 Jul 2026, 14:03" / "29 Tem 2026 14:03").
 *
 * The locale is passed in rather than left as `undefined`: the browser's own
 * locale is not the one the operator chose in the panel, so a Turkish panel
 * would otherwise print English month abbreviations.
 */
function fmtDate(iso: string, locale: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(locale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
}

export function RoadmapsScreen() {
  const roadmapsQuery = useRoadmaps();
  const del = useDeleteSimRoadmap();
  const navigate = useNavigate();
  // The selected roadmap lives in the URL (/roadmaps/:roadmapId) so it's
  // shareable, bookmarkable, and survives back/forward navigation.
  const { roadmapId } = useParams();
  const selected = roadmapId ?? null;
  const detailQuery = useRoadmapDetail(selected);
  const [showSource, setShowSource] = useState(false);
  const { t, locale } = useI18n();

  const roadmaps = roadmapsQuery.data?.items ?? [];
  const detail = detailQuery.data ?? null;
  const def = useMemo(() => (detail ? buildMermaid(detail) : ''), [detail]);

  // Default the URL to the newest roadmap once the list loads (replace, so the
  // bare /roadmaps entry doesn't linger in history).
  const firstId = roadmapsQuery.data?.items[0]?.id;
  useEffect(() => {
    if (!roadmapId && firstId) navigate(`/roadmaps/${firstId}`, { replace: true });
  }, [firstId, roadmapId, navigate]);

  if (roadmapsQuery.isError)
    return (
      <Text c="red" size="sm">
        {errorMessage(roadmapsQuery.error)}
      </Text>
    );

  return (
    <Stack gap="md">
      <Text c="dimmed" size="sm">
        {t('roadmaps.intro')}
      </Text>

      <Card withBorder padding="lg" radius="md">
        <Group align="flex-end" wrap="wrap">
          <Select
            label={t('roadmaps.select')}
            style={{ flex: 1, minWidth: 'min(320px, 100%)' }}
            value={selected}
            onChange={(v) => navigate(v ? `/roadmaps/${v}` : '/roadmaps')}
            placeholder={roadmaps.length === 0 ? t('roadmaps.none') : undefined}
            searchable
            data={roadmaps.map((r) => ({
              value: r.id,
              label: `${r.isSimulation ? '🧪 ' : ''}${r.title} — ${r.userEmail ?? r.userId} (${r.status} · ${fmtDate(r.createdAt, locale)})`,
            }))}
          />
          <Button variant="default" onClick={() => setShowSource((v) => !v)} disabled={!detail}>
            {t(showSource ? 'roadmaps.hideDiagram' : 'roadmaps.showDiagram')}
          </Button>
        </Group>
        {detailQuery.isLoading && (
          <Text c="dimmed" size="sm" mt="sm">
            {t('common.loading')}
          </Text>
        )}
        {detailQuery.isError && (
          <Text c="red" size="sm" mt="sm">
            {errorMessage(detailQuery.error)}
          </Text>
        )}
      </Card>

      {detail && (
        <Card withBorder padding="lg" radius="md">
          <Group justify="space-between" wrap="wrap" mb="xs">
            <div>
              <Text fw={600}>
                {detail.roadmap.isSimulation ? '🧪 ' : ''}
                {detail.roadmap.title}
              </Text>
              <Text c="dimmed" size="sm">
                {detail.roadmap.userEmail ?? detail.roadmap.userId} · {detail.roadmap.status} ·{' '}
                {t('roadmaps.createdAt', { date: fmtDate(detail.roadmap.createdAt, locale) })}
              </Text>
            </div>
            {detail.roadmap.isSimulation && (
              <Button
                color="red"
                variant="light"
                loading={del.isPending}
                onClick={() => {
                  if (!window.confirm(t('roadmaps.deleteSimConfirm'))) return;
                  del.mutate(detail.roadmap.id, {
                    onSuccess: () => navigate('/roadmaps', { replace: true }),
                  });
                }}
              >
                {t('roadmaps.deleteSim')}
              </Button>
            )}
          </Group>
          {del.isError && (
            <Text c="red" size="sm" mb="xs">
              {errorMessage(del.error)}
            </Text>
          )}
          <Group gap="xs" mb="md">
            <Badge variant="light">{t('roadmaps.stations', { count: detail.items.length })}</Badge>
            <Badge variant="light">
              {t('roadmaps.sessions', { count: detail.sessions.length })}
            </Badge>
            <Badge variant="light">
              {t('roadmaps.questionUses', {
                count: detail.sessions.reduce((n, s) => n + s.questions.length, 0),
              })}
            </Badge>
          </Group>
          <RoadmapView detail={detail} />
          <Collapse expanded={showSource}>
            <Card withBorder padding="md" radius="md" mt="md" bg="var(--mantine-color-body)">
              {def ? <Mermaid chart={def} /> : null}
            </Card>
          </Collapse>
        </Card>
      )}
    </Stack>
  );
}
